import React, { useState, useEffect } from 'react';
import { FileText, Calendar, User, AlertCircle, RefreshCw, Plus, Edit2, Pin } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { blogApi } from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';
import BlogEditor from '../../Admin/BlogEditor';

const NewsFeed = ({ theme = 'dark' }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editPostId, setEditPostId] = useState(null);
  
  const { user } = useAuth();
  const isAdmin = user?.is_admin || false;

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiPosts = await blogApi.listPublished();
      setPosts(apiPosts || []);
    } catch (err) {
      console.error('Error loading blog posts:', err);
      setError('Failed to load news feed');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const handleEditorClose = () => {
    setShowBlogEditor(false);
    setEditPostId(null);
    loadPosts(); // Refresh posts after editing
  };

  const handleAddPost = () => {
    setEditPostId(null);
    setShowBlogEditor(true);
  };

  const handleEditPost = (postId) => {
    setEditPostId(postId);
    setShowBlogEditor(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900">
        <div className="text-center text-white">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-400" />
          <p className="text-lg">Loading news feed...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-900">
        <div className="text-center text-white">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-semibold mb-2">Failed to load news feed</p>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gray-900">
      <div className="w-full p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Latest Updates</h1>
            <p className="text-gray-400">Stay updated with the latest news and announcements</p>
          </div>
          {isAdmin && (
            <button
              onClick={handleAddPost}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 
                text-white rounded-lg transition-colors"
            >
              <Plus size={18} />
              New Post
            </button>
          )}
        </div>

        {/* Posts */}
        <div className="space-y-6">
          {posts.map((post) => (
            <article
              key={post.post_id}
              className="bg-gray-800/50 rounded-lg border-l-4 border-l-blue-500 overflow-hidden"
            >
              {/* Post Header */}
              <div className="p-4 pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-white mb-2">
                      {post.title}
                      {post.is_pinned && (
                        <Pin size={16} className="inline-block ml-2 text-yellow-400" title="Pinned post" />
                      )}
                    </h2>
                    {post.excerpt && (
                      <p className="text-gray-300 text-sm mb-3">{post.excerpt}</p>
                    )}
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => handleEditPost(post.post_id)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 
                        rounded-lg transition-colors ml-4"
                      title="Edit post"
                    >
                      <Edit2 size={16} />
                    </button>
                  )}
                </div>
                
                <div className="flex items-center space-x-4 text-xs text-gray-400">
                  {post.author_name && (
                    <div className="flex items-center space-x-1">
                      <User size={12} />
                      <span>{post.author_name}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1">
                    <Calendar size={12} />
                    <span>{formatDate(post.published_at)}</span>
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <div className="px-4 pb-4">
                <div className="blog-content">
                  {(() => {
                    const content = post.content || '';
                    // Extract iframes from content for separate rendering
                    const iframeRegex = /<iframe[^>]*>[\s\S]*?<\/iframe>/gi;
                    const iframes = content.match(iframeRegex) || [];
                    // Remove iframes and embed containers from markdown content
                    const cleanContent = content
                      .replace(/<div class="embed-container">[\s\S]*?<\/div>/gi, '')
                      .replace(iframeRegex, '');
                    
                    return (
                      <>
                        <ReactMarkdown 
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            h1: ({children}) => <h1 className="text-2xl font-bold text-white mt-6 mb-3">{children}</h1>,
                            h2: ({children}) => <h2 className="text-xl font-semibold text-amber-400 mt-5 mb-2">{children}</h2>,
                            h3: ({children}) => <h3 className="text-lg font-medium text-blue-300 mt-4 mb-2">{children}</h3>,
                            p: ({children}) => <p className="text-gray-300 leading-relaxed mb-3">{children}</p>,
                            a: ({href, children}) => <a href={href} className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
                            ul: ({children}) => <ul className="list-disc list-inside text-gray-300 mb-3 space-y-1">{children}</ul>,
                            ol: ({children}) => <ol className="list-decimal list-inside text-gray-300 mb-3 space-y-1">{children}</ol>,
                            li: ({children}) => <li className="text-gray-300">{children}</li>,
                            code: ({inline, children}) => inline 
                              ? <code className="bg-gray-800 text-amber-300 px-1.5 py-0.5 rounded text-sm">{children}</code>
                              : <code className="block bg-gray-800 text-amber-300 p-3 rounded-lg overflow-x-auto text-sm">{children}</code>,
                            pre: ({children}) => <pre className="bg-gray-800 rounded-lg overflow-x-auto mb-3">{children}</pre>,
                            blockquote: ({children}) => <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-400 my-3">{children}</blockquote>,
                            strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                            em: ({children}) => <em className="text-gray-200 italic">{children}</em>,
                            hr: () => <hr className="border-gray-700 my-6" />,
                          }}
                        >
                          {cleanContent || '*No content*'}
                        </ReactMarkdown>
                        
                        {/* Render extracted iframes with dangerouslySetInnerHTML */}
                        {iframes.length > 0 && (
                          <div className="w-full my-8 flex flex-col items-center gap-6">
                            {iframes.map((iframe, index) => (
                              <div key={index} className="w-full max-w-3xl">
                                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                  <div 
                                    className="absolute top-0 left-0 w-full h-full rounded-lg overflow-hidden shadow-2xl"
                                    dangerouslySetInnerHTML={{ 
                                      __html: iframe
                                        .replace(/width="\d+"/g, 'width="100%"')
                                        .replace(/height="\d+"/g, 'height="100%"')
                                        .replace(/<iframe/g, '<iframe style="position:absolute;top:0;left:0;width:100%;height:100%"')
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </article>
          ))}
        </div>

        {posts.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-lg font-semibold text-white mb-2">No posts available</p>
            <p className="text-gray-400">Check back later for updates</p>
            {isAdmin && (
              <button
                onClick={handleAddPost}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 
                  text-white rounded-lg transition-colors inline-flex items-center gap-2"
              >
                <Plus size={18} />
                Create First Post
              </button>
            )}
          </div>
        )}
      </div>

      {/* Blog Editor Modal */}
      {showBlogEditor && (
        <BlogEditor
          isOpen={showBlogEditor}
          onClose={handleEditorClose}
          initialPostId={editPostId}
          theme={theme}
        />
      )}
    </div>
  );
};

export default NewsFeed;
